import { Buyer } from '@choco-admin/types';
import React, { useState, useCallback, useEffect } from 'react';
import { FiPlus, FiShoppingBag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  PageHeader,
  type PageHeaderAction,
} from '../../../../components/PageHeader';
import {
  type LoadingTableColumn,
  TableHeader as CustomTableHeader,
  LoadingTable,
} from '../../../../components/Table';
import { useDebounce } from '../../../../hooks';
import { useSearchBuyers } from '../../hooks';

import { BuyerListErrorState, BuyerTable, BuyerPagination } from './components';

export const BuyerList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const loadingTableColumns: LoadingTableColumn[] = [
    { label: 'Buyer', className: 'pl-6' },
    { label: 'Reference', className: 'hidden md:table-cell' },
    { label: 'Location', className: 'hidden lg:table-cell' },
    { label: 'Actions', width: 80, className: 'pr-6' },
  ];
  const {
    buyers,
    totalBuyers,
    isLoading,
    isFetching,
    error,
    searchBuyers,
    refetch,
  } = useSearchBuyers({
    initialPageSize: pageSize,
  });

  // Trigger search when parameters change
  useEffect(() => {
    searchBuyers({
      searchTerm: debouncedSearchTerm,
      page,
      pageSize,
    });
  }, [debouncedSearchTerm, page, pageSize, searchBuyers]);

  const handlePreviousPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  // Use buyers directly since server-side search is already applied
  const filteredBuyers = buyers;

  // Render the table content based on current state
  const renderTableContent = () => {
    if (error) {
      return <BuyerListErrorState error={error} refetch={refetch} />;
    }

    if (isLoading) {
      return (
        <LoadingTable
          columns={loadingTableColumns}
          ariaLabel="Buyers table"
          isLoading={true}
          isEmpty={false}
          data={[]}
          emptyState={{
            icon: FiShoppingBag,
            iconBgClass: 'bg-success-50',
            iconColorClass: 'text-success-500',
            title: 'Loading buyers...',
            description: 'Please wait while we load the buyers list.',
          }}
        />
      );
    }

    return <BuyerTable buyers={filteredBuyers as Buyer[]} />;
  };

  const pageActions: PageHeaderAction[] = [
    {
      label: 'Add Buyer',
      icon: <FiPlus size={16} />,
      onClick: () => navigate('/buyers/create'),
      color: 'primary',
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-background">
      <PageHeader
        title="Buyers Management"
        description="Manage buyer accounts and relationships"
        actions={pageActions}
      />

      <div className="space-y-6">
        <CustomTableHeader
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search buyers by name..."
          isLoading={isLoading}
        >
          <div className="text-sm text-default-600">
            <span className="font-medium">Page {page}</span>
            <span className="mx-2">·</span>
            <span>
              Showing{' '}
              <span className="font-medium">
                {isLoading ? '...' : filteredBuyers.length}
              </span>{' '}
              buyers
            </span>
            {isFetching && (
              <span className="ml-2 text-primary-500">(updating...)</span>
            )}
            {searchTerm !== debouncedSearchTerm && searchTerm.trim() !== '' && (
              <span className="ml-2 text-default-400">(typing...)</span>
            )}
          </div>
        </CustomTableHeader>

        {renderTableContent()}

        <BuyerPagination
          page={page}
          isLoading={isLoading}
          filteredBuyersCount={filteredBuyers.length}
          totalBuyers={totalBuyers}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      </div>
    </div>
  );
};
